import { imgixHost, isConsumer } from 'src/config'
import Studio from 'src/models/box-office-report/Studio'
import Generic from 'src/models/generic/Generic'
import Model, { encoders, executeMutation, GqlError, ID, Int, Integer, IsoDate, IsoDateTime, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/Model'
import MovieTerm from 'src/models/movie/MovieTerm'
import TitleClass from 'src/models/movie/TitleClass'
import PriceCard from 'src/models/showing/PriceCard'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import Site from 'src/models/site/Site'
import { prependWithConsumerRoot } from 'src/router/utils'

const MOVIE_TERMS_ACTIONS = Object.freeze({
  SETTLE: 'settle',
  UNSETTLE: 'unsettle',
  VALIDATE_SHOWING_TERMS: 'validate_showing_terms!'
})

export const MOVIE_QUERY_TYPE = {
  NOW_PLAYING_WITH_SHOWINGS: 'now-playing-and-coming-soon-movies-with-showings',
  NOW_PLAYING_AND_COMING_SOON: 'now-playing-and-coming-soon',
  NOW_PLAYING: 'now-playing',
  COMING_SOON: 'coming-soon',
  MORE_FILMS: 'more-films',
  MY_LISTS: 'my-lists'
}
export default class Movie extends Model {
  static name = 'Movie'

  static deletable = true

  static duplicatable = true

  static versioned = true

  static validatable = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmProgramming]

  static ignoreDisplayOrder = true

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      name: {
        type: String,
        default: '',
        sortable: true,
        translatable: true
      },
      abbreviation: {
        type: String,
        computed () {
          if (this.isRemote) return this.data.abbreviation

          return _.toUpper(this.name.replace(/^the\s+/i, '').replace(/\W/g, '').substring(0, 4), 4)
        }
      },
      site: {
        type: Site,
        default: null,
        reference: true,
        output: true
      },
      showingStatus: {
        type: String,
        output: true,
        sortable: true
      },
      displayMetaData: {
        type: JsonObject,
        output: true
      },
      urlSlug: {
        type: String,
        default: null,
        sortable: true
      },
      urlSlugIsReadonly: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },
      studio: {
        type: Studio,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      posterImage: {
        type: String,
        default: null,
        sortable: true,
        translatable: true
      },
      posterImageParams: {
        type: String,
        default: 'fit=crop&w=400&h=600&fm=jpeg&auto=format&cs=origin',
        local: true
      },
      posterImageSmallParams: {
        type: String,
        default: 'fit=crop&w=60&h=90&fm=jpeg&auto=format&cs=origin',
        local: true
      },
      posterImageLargeParams: {
        type: String,
        default: 'fit=crop&w=1000&h=1500&fm=jpeg&auto=format&cs=origin',
        local: true
      },
      signageDisplayPoster: {
        type: Boolean,
        default: false
      },
      bannerImage: {
        type: String,
        default: null,
        sortable: true,
        translatable: true
      },
      bannerImageParams: {
        type: String,
        default: 'fit=crop&w=1000&height=500&fm=jpeg&auto=format&cs=origin',
        local: true
      },
      bannerImageSmallParams: {
        type: String,
        default: 'fit=crop&w=120&h=60&fm=jpeg&auto=format&cs=origin',
        local: true
      },
      signageDisplayBanner: {
        type: Boolean,
        default: false
      },
      animatedPosterVideo: {
        type: String,
        default: null,
        translatable: true
      },
      signageDisplayAnimatedPoster: {
        type: Boolean,
        default: false
      },
      signageMessageOverride: {
        type: String,
        default: null,
        translatable: true
      },
      color: {
        type: String,
        sortable: true
      },
      synopsis: {
        type: String,
        translatable: true
      },
      starring: {
        type: String
      },
      directedBy: {
        type: String
      },
      producedBy: {
        type: String
      },
      searchTerms: {
        type: String
      },
      duration: {
        type: Integer
      },
      genre: {
        type: String,
        translatable: true
      },
      allGenres: {
        type: String,
        translatable: true
      },
      titleClass: {
        type: TitleClass,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      rating: {
        type: String
      },
      ratingReason: {
        type: String,
        translatable: true
      },
      trailerYoutubeId: {
        type: String,
        translatable: true
      },
      trailerVideo: {
        type: String,
        translatable: true
      },
      signageDisplayTrailer: {
        type: Boolean,
        default: false
      },
      releaseDate: {
        type: IsoDate,
        sortable: true
      },
      dateOfFirstShowing: {
        type: IsoDate,
        sortable: true
      },
      overrideDateOfFirstShowing: {
        type: Boolean,
        default: false
      },
      hideDateOfFirstShowing: {
        type: Boolean,
        default: false
      },
      embargoShowingLiftedAt: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      embargoPurchaseLiftedAt: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      allowPrivateSalesOnExternal: {
        type: Boolean,
        default: false
      },
      isMarathon: {
        type: Boolean,
        default: false
      },
      marathonMovies: {
        type: [Movie],
        default: () => ([]),
        encode (data) {
          if (this.isMarathon) {
            data.marathonMovieIds = _.map(this.marathonMovies.data, 'id')
          } else {
            data.marathonMovieIds = []
          }
        },
        reference: true
      },
      recommendedMovies: {
        type: [Movie],
        default: () => ([]),
        encode (data) {
          data.recommendedMovieIds = _.map(this.recommendedMovies.data, 'id')
        }
      },
      predictedWeekOneTicketSales: {
        type: Number,
        output: true
      },
      tmdbPopularityScore: {
        type: Number,
        sortable: true,
        output: true
      },
      tmdbId: {
        type: String
      },
      willIncludeInComingSoon: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      includeInComingSoon: {
        type: Boolean,
        default: true
      },
      includeInFuture: {
        type: Boolean,
        default: false
      },
      overridePriceCard: {
        type: Boolean,
        default: false
      },
      customPriceCard: {
        type: PriceCard,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      overridePostStartTimeBufferMinutes: {
        type: Boolean,
        default: false
      },
      customPostStartTimeBufferMinutes: {
        type: Integer,
        default: 10
      },
      showingBadges: {
        type: [ShowingBadge],
        default: () => { return [] },
        encode (data) {
          data.showingBadgeIds = _.map(this.showingBadges.data, 'id')
        },
        reference: true
      },
      datesWithPublicShowing: {
        type: [String],
        output: true,
        excludeFromDefault: true,
        default () {
          return []
        }
      },
      allDatesWithPublicShowing: {
        type: [String],
        output: true,
        excludeFromDefault: true,
        default () {
          return []
        }
      },
      datesWithShowing: {
        type: [String],
        output: true,
        excludeFromDefault: true,
        default () {
          return []
        }
      },
      sendRentrak: {
        type: Boolean,
        default: true,
        encode (data) {
          if (this.isMarathon) {
            data.sendRentrak = false
          } else {
            data.sendRentrak = this.data.sendRentrak
          }
        }
      },
      rentrakName: {
        type: String,
        default: null
      },

      action: {
        type: String,
        input: true,
        default: null
      },

      libraryParentId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        excludeFromDefault: true
      },
      libraryChildSiteIds: {
        type: [ID],
        output: true,
        excludeFromDefault: true
      },
      libraryDescendantSites: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      libraryChildrenShowingCount: {
        type: Integer,
        output: true
      },
      showingCount: {
        type: Integer,
        output: true
      },
      allowPastSales: {
        type: Boolean
      },
      dcmEdiMovieId: {
        type: String,
        default: null
      },
      dcmEdiMovieName: {
        type: String,
        default: null,
        output: true
      },
      disableOnlineConcessions: {
        type: Boolean,
        default: false
      },
      movieTerms: {
        type: [MovieTerm],
        default: () => ([]),
        allowDestroy: true,
        acceptsNestedAttributes: true
      },
      slide: {
        type: Number,
        default: 0,
        local: true
      },
      overrideMaxTicketsPerOrderPerShowing: {
        type: Boolean,
        default: false
      },
      maxTicketsPerOrderPerShowing: {
        type: Integer,
        default: 10
      },
      displayOrder: {
        type: Integer,
        default: null
      },
      displayOrderNext: {
        type: Integer,
        default: null
      }
    }

    this.computed = {
      posterImageUrl () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?${this.posterImageParams}`
      },
      posterImageUrlNoParams () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?auto=format`
      },
      posterImageSmallUrl () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?${this.posterImageSmallParams}`
      },
      posterImageLargeUrl () {
        if (!this.posterImage) return ''

        return `${imgixHost}/${this.posterImage}?${this.posterImageLargeParams}`
      },
      bannerImageUrl () {
        if (!this.bannerImage) return

        return `${imgixHost}/${this.bannerImage}?${this.bannerImageParams}`
      },
      bannerImageSmallUrl () {
        if (!this.bannerImage) return

        return `${imgixHost}/${this.bannerImage}?${this.bannerImageSmallParams}`
      },
      animatedPosterVideoUrl () {
        if (!this.animatedPosterVideo) return

        return `${imgixHost}/${this.animatedPosterVideo}?auto=format`
      },
      trailerVideoUrl () {
        if (!this.trailerVideo) return ''

        return `${imgixHost}/${this.trailerVideo}?auto=format`
      },
      future () {
        return this.dateOfFirstShowing > new IsoDate()
      },
      synopsisWithBreaks () {
        return this.synopsis?.replace(/\n/g, '<br>') || ''
      },
      urlSlugOrId () {
        return this.urlSlug || this.id
      },
      hasPlayedOrPlayingToday () {
        if (!this.allDatesWithPublicShowing) return null
        return _.find(this.allDatesWithPublicShowing, date => {
          const parsedDate = IsoDate.parse(date)
          const dateNow = new IsoDate()
          if (parsedDate <= dateNow) return true

          return false
        })
      },
      // Often we want to sort movies by their "showing status", but we exclude this from this magic sort as it is not always relevant.
      dayOfMagicSortValue () {
        if (!this.displayMetaData?.magicSortValue) return null
        const magicSortValue = this.displayMetaData?.magicSortValue.toString().padStart(11, '0')
        const value = (this.displayOrder || 99) + `${magicSortValue}`.substring(3)
        return Number(value)
      },
      willSendRentrak () {
        return this.studio?.sendRentrak && this.sendRentrak
      }
    }

    this.fragments = {
      select: ['id', 'name', 'color']
    }

    this.queries = {
      currentMovies: {
        arguments: {
          resultVersion: {
            type: String,
            required: false
          }
        },
        results: {
          movies: [Movie]
        }
      },
      upcomingMovies: {
        arguments: {
          resultVersion: {
            type: String,
            required: false
          }
        },
        results: {
          movies: [Movie]
        }
      },
      currentAndUpcomingMovies: {
        arguments: {
          resultVersion: {
            type: String,
            required: false
          }
        },
        results: {
          movies: [Movie]
        }
      },
      findMovieBySlug: {
        arguments: {
          urlSlug: {
            type: String,
            required: true
          },
          siteIds: {
            type: [ID],
            required: false
          }
        },
        results: {
          movies: Movie
        }
      },
      // We should only use this to lookup ids for movies
      findMoviesBySlug: {
        arguments: {
          urlSlug: {
            type: String,
            required: true
          },
          siteIds: {
            type: [ID],
            required: false
          }
        },
        results: {
          movies: [Movie]
        }
      }
    }

    this.mutations = {
      updateDisplayOrder: {
        arguments: {
          movieId: {
            type: ID,
            required: true
          },
          displayOrder: {
            type: Int,
            required: false
          },
          displayOrderNext: {
            type: Int,
            required: false
          }
        },
        results: {
          movie: Movie,
          errors: [GqlError]
        }
      },
      addOrUpdateMovieTerms: {
        arguments: {
          movieInput: {
            type: this,
            required: true
          }
        },
        results: {
          movie: Movie,
          errors: [GqlError]
        }
      },
      actionMovieTerms: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          action: {
            type: String,
            required: true
          }
        },
        results: {
          actionResult: Generic,
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
    this.filterable = true
  }

  get pageUrl () {
    return this.site ? this.site.url(this.pagePath) : this.pagePath
  }

  get pagePath () {
    const path = `/movie/${this.urlSlugOrId}`
    return isConsumer ? prependWithConsumerRoot(path) : path
  }

  get formattedDuration () {
    if (!this.duration) return null
    const hours = Math.floor(this.duration / 60)
    const minutes = this.duration % 60

    return (hours > 0 ? `${hours} hr ` : '') + (`${minutes} min`)
  }

  get hasTerms () {
    if (!this.movieTerms?.data || this.movieTerms?.data?.length === 0) return false

    return this.movieTerms.data[0].hasFlatFee ||
           this.movieTerms.data[0].weeklyTerms.length > 1 ||
           this.movieTerms.data[0].weeklyTerms[0] !== 0
  }

  get magic () {
    return this.displayMetaData?.magicSortValue
  }

  get trackingData () {
    return {
      movieId: this.id,
      movieName: this.name,
      movieSlug: this.urlSlugOrId,
      movieGenre: this.genre,
      movieRating: this.rating,
      titleClassId: this.titleClass?.id,
      titleClassName: this.titleClass?.name,
      showingBadges: this.displayMetaData?.everyShowingsBadges
    }
  }

  get releaseDateText () {
    if (!this.releaseDate) return 'Release TBC'
    return this.releaseDate.i18n('date')
  }

  async syncWithTmdb (override = false) {
    this.action = override ? 'overwrite_from_tmdb' : 'sync_with_tmdb'
    await this.save()
    this.resetField('action')
  }

  async updateDisplayOrder () {
    const results = await executeMutation(
      'updateDisplayOrder',
      {
        movieId: this.id,
        displayOrder: this.displayOrder,
        displayOrderNext: this.displayOrderNext
      },
      {
        movie: {
          structure: {
            id: true,
            displayOrder: true,
            displayOrderNext: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async addOrUpdateMovieTerms () {
    const results = await executeMutation(
      'addOrUpdateMovieTerms',
      {
        movieInput: this.deepEncode
      },
      {
        movie: {
          queryName: 'movie',
          key: 'movie',
          structure: {
            id: true,
            abbreviation: true,
            dateOfFirstShowing: true,
            overrideDateOfFirstShowing: true,
            movieTerms: {
              id: true,
              abbreviation: true,
              priorityOrder: true,
              hasFlatFee: true,
              flatFee: true,
              minimumGuarantee: true,
              deposit: true,
              showingCount: true,
              showingBadgeIds: true,
              movieTermWeeks: {
                id: true,
                type: true,
                weekNumber: true,
                termPercentHundredths: true
              }
            }
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    if (_.isEmpty(results.errors)) this.setData(results.data.movie, true)

    return results
  }

  async validateShowingTerms () {
    const results = await executeMutation(
      'actionMovieTerms',
      {
        id: this.id,
        action: MOVIE_TERMS_ACTIONS.VALIDATE_SHOWING_TERMS
      },
      {
        actionResult: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async settle () {
    const results = await executeMutation(
      'actionMovieTerms',
      {
        id: this.id,
        action: MOVIE_TERMS_ACTIONS.SETTLE
      },
      {
        actionResult: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async unsettle () {
    const results = await executeMutation(
      'actionMovieTerms',
      {
        id: this.id,
        action: MOVIE_TERMS_ACTIONS.UNSETTLE
      },
      {
        actionResult: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }
}
